/**
 * 모듈 행 정렬 — 도메인 키 추출기/옵션 (sortFilter 순수 코어에 주입). **팔레트/합성/융합 탭 공용**.
 * 모양별(shapeId, SHAPE_CATALOG 정의 순서) / 파츠별(이름, localeCompare) / 등급별(rarity, RARITY_ORDER 순).
 *
 * 정렬에 필요한 최소 필드만 {@link SortableModuleRow}로 요구한다 — PaletteRow·ModuleMergeRow·ModuleFusionRow가
 * 모두 구조적으로 이를 만족하므로(instanceId/name/rarity/shapeId 보유) 동일 추출기를 그대로 재사용한다.
 */
import type { KeyExtractors, SortState } from "./sortFilter.js";
import { SHAPE_CATALOG } from "../data/moduleShapes.js";
import { RARITY_ORDER } from "../modules/ProjectileModule.js";

/** 정렬에 필요한 모듈 행 최소 계약 — 팔레트/합성/융합 행이 모두 만족(구조적 타이핑). */
export interface SortableModuleRow {
  readonly instanceId: string;
  readonly name: string;
  readonly rarity: string;
  readonly shapeId: string;
}

/** 팔레트 정렬 키 — 드롭다운 옵션 + i18n `sort.<key>` 키와 1:1. */
export type PaletteSortKey = "shape" | "name" | "rarity";

/** 드롭다운 옵션 순서(표시 순). */
export const PALETTE_SORT_KEYS: readonly PaletteSortKey[] = ["shape", "name", "rarity"];

/** 기본 정렬 상태 — 등급 오름차순(common→transcendent). */
export const DEFAULT_PALETTE_SORT: SortState<PaletteSortKey> = { key: "rarity", dir: "asc" };

// 모양 정의 순서 인덱스 — 미등록 shapeId는 맨 뒤(catalog 길이)로.
const SHAPE_ORDER = Object.keys(SHAPE_CATALOG);
const shapeIndex = (shapeId: string): number => {
  const i = SHAPE_ORDER.indexOf(shapeId);
  return i < 0 ? SHAPE_ORDER.length : i;
};
const rarityIndex = (rarity: string): number => {
  const i = RARITY_ORDER.indexOf(rarity as (typeof RARITY_ORDER)[number]);
  return i < 0 ? RARITY_ORDER.length : i;
};

/** 모듈 행 키 추출기. shape/rarity는 정의 순서 인덱스(number), name은 문자열(localeCompare). */
export const PALETTE_KEY_EXTRACTORS: KeyExtractors<SortableModuleRow, PaletteSortKey> = {
  shape: (r) => shapeIndex(r.shapeId),
  name: (r) => r.name,
  rarity: (r) => rarityIndex(r.rarity),
};

/** 동률 tie-break — instanceId로 결정적 순서(테스트/표시 안정). */
export const paletteTieBreak = (a: SortableModuleRow, b: SortableModuleRow): number =>
  a.instanceId.localeCompare(b.instanceId);
